import React, { useCallback, useRef, useState, useMemo } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch,
    Lookup,
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { LoadPanel } from "devextreme-react/load-panel";
import notify from "devextreme/ui/notify";
import data from "../../../../../assets/apiRoutes.json";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// STATUS flag dropdown options. Mirrors the legacy Abecas Win client where the
// column renders the flag's description rather than the raw character.
const STATUS_OPTIONS = [
    { id: "N", text: "No, Do Not Generate" },
    { id: "Y", text: "Yes, Generate" },
];

function SpecialActionsTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    const serialNumberRowId = props?.formFields?.ROWID;
    const hasParent = !!serialNumberRowId && serialNumberRowId !== EMPTY_GUID;

    const gridRef = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const gridKey = "C7B6A5D4-3E2F-1B0A-9C8D-7E6F5A4B3C2D";
    const position = { of: "#accordion" };

    const hideLoadPanel = useCallback(() => setLoadPanelVisible(false), []);
    const loadState = useCallback(() => null, []);
    const saveState = useCallback(() => { }, []);

    // WMCODES lookups for the Code 1..5 columns, each scoped server-side to its
    // own CODETYPE ('S1'..'S5'). Same endpoint backs WMCodesLookupLayout in
    // dynamic forms.
    const codeLookupStores = useMemo(() => {
        const make = (codeType) => createStore({
            key: "code",
            loadUrl: `${window._env_.REACT_APP_APIS}/wm/Lookup/GetCodes`,
            loadParams: { codeType },
            onBeforeSend: function (method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                    access_token: accessToken,
                };
            },
        });
        return {
            S1: make("S1"),
            S2: make("S2"),
            S3: make("S3"),
            S4: make("S4"),
            S5: make("S5"),
        };
    }, [accessToken]);

    const dataSource = useMemo(() => {
        if (!hasParent) {
            return new DataSource({ store: [], key: "RowId" });
        }

        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.SpecialActions.GetSpecialActionServices}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.SpecialActions.InsertSpecialAction}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.SpecialActions.UpdateSpecialAction}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.SpecialActions.DeleteSpecialAction}`,
                loadParams: {
                    serialNumberRowId: serialNumberRowId,
                },
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };

                    if (method === "insert" || method === "update") {
                        ajaxOptions.method = method === "update" ? "PUT" : "POST";
                        ajaxOptions.headers["Content-Type"] = "application/json";

                        let valuesObj = {};
                        let key = null;
                        if (ajaxOptions.data) {
                            key = ajaxOptions.data.key;
                            if (ajaxOptions.data.values) {
                                valuesObj = typeof ajaxOptions.data.values === "string"
                                    ? JSON.parse(ajaxOptions.data.values)
                                    : ajaxOptions.data.values;
                            }
                        }

                        const payload = {
                            Rowid: method === "update" ? key : EMPTY_GUID,
                            RSerialNumber: serialNumberRowId,
                            Status: valuesObj.Status ?? null,
                            NetWeight: valuesObj.NetWeight ?? null,
                            GrossWeight: valuesObj.GrossWeight ?? null,
                            BillAsQuantity: valuesObj.BillAsQuantity ?? null,
                            Comment: valuesObj.Comment ?? null,
                            UserDefinedComment: valuesObj.UserDefinedComment ?? null,
                            UserDefinedCode1: valuesObj.UserDefinedCode1 ?? null,
                            UserDefinedCode2: valuesObj.UserDefinedCode2 ?? null,
                            UserDefinedCode3: valuesObj.UserDefinedCode3 ?? null,
                            UserDefinedCode4: valuesObj.UserDefinedCode4 ?? null,
                            UserDefinedCode5: valuesObj.UserDefinedCode5 ?? null,
                        };
                        ajaxOptions.data = JSON.stringify(payload);
                    }

                    if (method === "delete") {
                        ajaxOptions.method = "DELETE";
                    }
                },
                onInserted: function () {
                    notify("Special Action inserted successfully.", "success", 3000);
                    setTimeout(() => gridRef.current?.instance()?.refresh(), 100);
                },
                onUpdated: function () {
                    notify("Special Action updated successfully.", "success", 3000);
                    setTimeout(() => gridRef.current?.instance()?.refresh(), 100);
                },
                onRemoved: function () {
                    notify("Special Action deleted successfully.", "success", 3000);
                    setTimeout(() => gridRef.current?.instance()?.refresh(), 100);
                },
                onInsertError: function (error) {
                    const msg = error?.message || error?.responseJSON?.message || "Failed to insert Special Action.";
                    notify(msg, "error", 5000);
                },
                onUpdateError: function (error) {
                    const msg = error?.message || error?.responseJSON?.message || "Failed to update Special Action.";
                    notify(msg, "error", 5000);
                },
                onRemoveError: function (error) {
                    const msg = error?.message || error?.responseJSON?.message || "Failed to delete Special Action.";
                    notify(msg, "error", 5000);
                },
            }),
            key: "Rowid",
            paginate: false,
        });
    }, [hasParent, serialNumberRowId, accessToken]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedKeys(e.selectedRowKeys);
    }, []);

    const onInitNewRow = useCallback((e) => {
        e.data.Rowid = EMPTY_GUID;
        e.data.RSerialNumber = serialNumberRowId;
        e.data.Status = "N";
        e.data.NetWeight = 0;
        e.data.GrossWeight = 0;
        e.data.BillAsQuantity = 0;
        e.data.Comment = "";
        e.data.UserDefinedComment = "";
        e.data.UserDefinedCode1 = "";
        e.data.UserDefinedCode2 = "";
        e.data.UserDefinedCode3 = "";
        e.data.UserDefinedCode4 = "";
        e.data.UserDefinedCode5 = "";
    }, [serialNumberRowId]);

    const onEditingStart = useCallback((e) => {
        if (e.key && !gridRef.current?.instance()?.isRowSelected(e.key)) {
            gridRef.current.instance().selectRows([e.key], false);
        }
    }, []);

    return (
        <div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />

            <DataGrid
                ref={gridRef}
                dataSource={dataSource}
                selectedRowKeys={selectedKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onSelectionChanged}
                onInitNewRow={onInitNewRow}
                onEditingStart={onEditingStart}
                height="55vh"
                noDataText={hasParent ? "No special actions found." : "Save the Serial Number record before adding Special Actions."}
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                    <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                </ColumnChooser>
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={hasParent}
                    allowUpdating={hasParent}
                    allowDeleting={hasParent}
                    newRowPosition="last"
                    confirmDelete={true}
                    texts={{ confirmDeleteMessage: "Are you sure you want to delete this Special Action?" }}
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="Rowid" caption="Row ID" visible={false} />
                <Column dataField="RSerialNumber" caption="Serial Number ID" visible={false} />

                <Column
                    dataField="Status"
                    caption="Status"
                    dataType="string"
                    allowSorting={false}
                >
                    <Lookup dataSource={STATUS_OPTIONS} valueExpr="id" displayExpr="text" />
                </Column>
                <Column
                    dataField="NetWeight"
                    caption="Net Weight"
                    dataType="number"
                    format="#,##0.0000"
                    allowSorting={false}
                />
                <Column
                    dataField="GrossWeight"
                    caption="Gross Weight"
                    dataType="number"
                    format="#,##0.0000"
                    allowSorting={false}
                />
                <Column
                    dataField="BillAsQuantity"
                    caption="Bill As"
                    dataType="number"
                    format="#,##0.0000"
                    allowSorting={false}
                />
                <Column
                    dataField="Comment"
                    caption="Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="UserDefinedComment"
                    caption="User Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="UserDefinedCode1"
                    caption="Code 1"
                    dataType="string"
                    visible={false}
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={codeLookupStores.S1}
                        valueExpr="code"
                        displayExpr="code"
                    />
                </Column>
                <Column
                    dataField="UserDefinedCode2"
                    caption="Code 2"
                    dataType="string"
                    visible={false}
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={codeLookupStores.S2}
                        valueExpr="code"
                        displayExpr="code"
                    />
                </Column>
                <Column
                    dataField="UserDefinedCode3"
                    caption="Code 3"
                    dataType="string"
                    visible={false}
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={codeLookupStores.S3}
                        valueExpr="code"
                        displayExpr="code"
                    />
                </Column>
                <Column
                    dataField="UserDefinedCode4"
                    caption="Code 4"
                    dataType="string"
                    visible={false}
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={codeLookupStores.S4}
                        valueExpr="code"
                        displayExpr="code"
                    />
                </Column>
                <Column
                    dataField="UserDefinedCode5"
                    caption="Code 5"
                    dataType="string"
                    visible={false}
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={codeLookupStores.S5}
                        valueExpr="code"
                        displayExpr="code"
                    />
                </Column>

                <Toolbar>
                    <Item name="addRowButton" />
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SpecialActionsTab);
